import { useState } from "react";
import { ArrowRight, CheckCircle2, PackageSearch } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const MILESTONES = ["Pickup", "Processing", "In Transit", "Customs", "Out for Delivery", "Delivered"];

function milestoneIndex(status) {
  const t = (status || "").toLowerCase();
  if (!t) return -1;
  if (t.includes("out for")) return 4;
  if (t.includes("delivered")) return 5;
  if (t.includes("custom")) return 3;
  if (t.includes("transit") || t.includes("hub") || t.includes("departed") || t.includes("arrived")) return 2;
  if (t.includes("process") || t.includes("reached") || t.includes("manifest")) return 1;
  return 0;
}

export default function TrackingSection() {
  const [awb, setAwb] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const track = async (e) => {
    e.preventDefault();
    if (!awb.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await api.get(`/public/track/${awb.trim().toUpperCase()}`);
      setResult(res.data);
    } catch {
      toast.error("Tracking number not found — please check the AWB and try again.");
    } finally {
      setBusy(false);
    }
  };

  const status = result?.status || result?.shipment?.status || "";
  const active = result ? milestoneIndex(status) : -1;

  return (
    <section className="bg-[#F4F3F1] py-20 sm:py-28" data-testid="homepage-live-tracking-panel">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
        <p className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
          <span className="h-2 w-2 rounded-full bg-saffron" /> Real-time tracking
        </p>
        <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-navy sm:text-5xl">
          Where is your <span className="text-saffron">shipment?</span>
        </h2>
        <p className="mt-4 text-sm text-slate-500 sm:text-base">Track your shipment from pickup to delivery — live scan-level updates.</p>
        <form onSubmit={track} className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row" data-testid="tracking-section-form">
          <input value={awb} onChange={(e) => setAwb(e.target.value)} data-testid="tracking-section-awb-input"
                 placeholder="Enter AWB Number (e.g. IFAST1000001)"
                 className="flex-1 rounded-full border border-stone-300 bg-white px-6 py-4 text-sm font-medium text-navy placeholder:text-slate-400 outline-none transition-colors focus:border-saffron" />
          <button type="submit" disabled={busy} data-testid="tracking-section-track-btn"
                  className="group flex items-center justify-center gap-2 rounded-full bg-saffron px-8 py-4 text-sm font-bold text-white transition-colors hover:bg-saffron-hover disabled:opacity-60">
            <PackageSearch className="h-4 w-4" /> {busy ? "Tracking…" : "Track Now"}
          </button>
        </form>

        {result && (
          <div className="mt-10 rounded-3xl border border-stone-200 bg-white p-6 text-left shadow-xl shadow-stone-200 sm:p-8" data-testid="tracking-section-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">AWB {result.awb || awb.toUpperCase()}</p>
                <p className="mt-1 font-heading text-xl font-semibold text-navy">{status || "Shipment found"}</p>
              </div>
              <a href={`/track/${encodeURIComponent((result.awb || awb).toUpperCase())}`} data-testid="tracking-section-full-link"
                 className="group inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-stone-50 px-4 py-2 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
                Full timeline <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
            <div className="mt-7 flex items-center">
              {MILESTONES.map((m, i) => (
                <div key={m} className="flex flex-1 items-center last:flex-none" data-testid={`tracking-milestone-${i}`}>
                  <div className="flex flex-col items-center gap-2">
                    <span className={`grid h-8 w-8 place-items-center rounded-full border-2 text-[10px] font-black transition-colors ${i <= active ? "border-saffron bg-saffron text-white" : "border-stone-300 bg-stone-50 text-slate-400"}`}>
                      {i < active ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                    </span>
                    <span className={`hidden text-[10px] font-bold uppercase tracking-wide sm:block ${i <= active ? "text-saffron" : "text-slate-400"}`}>{m}</span>
                  </div>
                  {i < MILESTONES.length - 1 && <div className={`mx-1 h-0.5 flex-1 transition-colors sm:mx-2 ${i < active ? "bg-saffron" : "bg-stone-200"}`} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
